import React, { useEffect, useRef, useState, useMemo } from "react";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import heroImage from "../assets/hero_image_desktop.png";
import heroImageMobile from "../assets/hero_image.png";
import brandLogo from "../assets/brand_logo.png";
import { songList } from "../utils/songList";

const InitialLoader = ({ progress }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#fffaf3]/95 backdrop-blur-sm">
    <div className="relative flex flex-col items-center gap-6">
      <img className="w-44" src={brandLogo} alt="Brand Logo" />

      <div className="w-64 md:w-80">
        <div className="h-1.5 bg-[#dcc4a7]/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#d89a2b] to-[#f1c56a] rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="text-center">
        <p className="text-[#7a5b49] text-xs mt-1">
          Loading some awesome sound tracks for you...
        </p>
      </div>
    </div>
  </div>
);

function HomePage() {
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);

  const audioRef = useRef(new Audio());
  const isSeekingRef = useRef(false);
  const loadingIntervalRef = useRef(null);

  useEffect(() => {
    const startTime = Date.now();
    const duration = 3000;

    loadingIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const nextProgress = Math.min((elapsed / duration) * 100, 100);
      setLoadProgress(nextProgress);

      if (nextProgress >= 100) {
        clearInterval(loadingIntervalRef.current);
        setIsLoading(false);
      }
    }, 16);

    const timeoutId = setTimeout(() => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        setIsLoading(false);
      }
    }, 3500);

    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
      clearTimeout(timeoutId);
    };
  }, []);

  const languages = useMemo(() => {
    const uniqueLanguages = [...new Set(songList.map((song) => song.language))];
    const customOrder = ["English", "Hindi"];

    const orderedLanguages = [];
    const otherLanguages = [];

    uniqueLanguages.forEach((lang) => {
      if (customOrder.includes(lang)) {
        orderedLanguages.push(lang);
      } else {
        otherLanguages.push(lang);
      }
    });

    orderedLanguages.sort(
      (a, b) => customOrder.indexOf(a) - customOrder.indexOf(b),
    );
    otherLanguages.sort();

    return [...new Set([...orderedLanguages, ...otherLanguages])];
  }, []);

  const filteredSongs = useMemo(() => {
    return songList.filter((song) => song.language === selectedLanguage);
  }, [selectedLanguage]);

  // useEffect(() => {
  //   const threshold = 160;

  //   const isMobile = () =>
  //     /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(
  //       navigator.userAgent,
  //     ) || window.innerWidth < 768;

  //   const redirectAway = () => {
  //     const audio = audioRef.current;
  //     audio.pause();
  //     audio.currentTime = 0;
  //     setIsPlaying(false);
  //     setCurrentlyPlayingIndex(null);
  //     navigate("/warning", { replace: true });
  //   };

  //   const sizeGapOpen = () =>
  //     window.outerWidth - window.innerWidth > threshold ||
  //     window.outerHeight - window.innerHeight > threshold;

  //   const timingOpen = () => {
  //     const start = performance.now();
  //     debugger; // eslint-disable-line no-debugger
  //     const end = performance.now();
  //     return end - start > 50;
  //   };

  //   const detectDevTools = () => {
  //     if (isMobile()) return;
  //     if (sizeGapOpen() || timingOpen()) redirectAway();
  //   };

  //   detectDevTools();
  //   const t = setTimeout(detectDevTools, 300);

  //   window.addEventListener("resize", detectDevTools);
  //   const interval = setInterval(detectDevTools, 800);

  //   return () => {
  //     clearTimeout(t);
  //     clearInterval(interval);
  //     window.removeEventListener("resize", detectDevTools);
  //   };
  // }, [navigate]);

  const stopAudio = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;

    setIsPlaying(false);
    setCurrentlyPlayingIndex(null);
    setProgress(0);
    setCurrentTime(0);
  };

  useEffect(() => {
    if (currentlyPlayingIndex !== null) {
      const currentSong = songList[currentlyPlayingIndex];
      if (currentSong?.language !== selectedLanguage) {
        stopAudio();
      }
    }
  }, [selectedLanguage, currentlyPlayingIndex]);

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (isSeekingRef.current) return;

      const dur = audio.duration || 0;
      const ct = audio.currentTime || 0;

      setDuration(dur);
      setCurrentTime(ct);
      setProgress(dur ? (ct / dur) * 100 : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentlyPlayingIndex(null);
      setProgress(0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  const playSong = async (filteredIndex) => {
    const audio = audioRef.current;

    const actualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[filteredIndex].id,
    );

    try {
      if (currentlyPlayingIndex === actualIndex) {
        if (isPlaying) {
          audio.pause();
          setIsPlaying(false);
        } else {
          await audio.play();
          setIsPlaying(true);
        }
        return;
      }

      audio.pause();
      audio.currentTime = 0;
      audio.src = filteredSongs[filteredIndex].url;
      await audio.play();

      setCurrentlyPlayingIndex(actualIndex);
      setIsPlaying(true);
    } catch (err) {
      console.warn("Playback error:", err);
    }
  };

  const goNext = () => {
    if (currentlyPlayingIndex === null) return;

    const currentFilteredIndex = filteredSongs.findIndex(
      (song) => song.id === songList[currentlyPlayingIndex].id,
    );

    if (currentFilteredIndex === -1) return;

    const nextFilteredIndex =
      currentFilteredIndex === filteredSongs.length - 1
        ? 0
        : currentFilteredIndex + 1;

    const nextActualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[nextFilteredIndex].id,
    );

    playSongByActualIndex(nextActualIndex);
  };

  const goPrev = () => {
    if (currentlyPlayingIndex === null) return;

    const currentFilteredIndex = filteredSongs.findIndex(
      (song) => song.id === songList[currentlyPlayingIndex].id,
    );

    if (currentFilteredIndex === -1) return;

    const prevFilteredIndex =
      currentFilteredIndex === 0
        ? filteredSongs.length - 1
        : currentFilteredIndex - 1;

    const prevActualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[prevFilteredIndex].id,
    );

    playSongByActualIndex(prevActualIndex);
  };

  const playSongByActualIndex = (actualIndex) => {
    const audio = audioRef.current;

    audio.pause();
    audio.currentTime = 0;
    audio.src = songList[actualIndex].url;
    audio.play();

    setCurrentlyPlayingIndex(actualIndex);
    setIsPlaying(true);
  };

  const formatTime = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e) => {
    const val = Number(e.target.value);
    const audio = audioRef.current;

    const newTime = (val / 100) * audio.duration;
    audio.currentTime = newTime;

    setProgress(val);
  };

  const currentSong = useMemo(() => {
    return currentlyPlayingIndex !== null
      ? songList[currentlyPlayingIndex]
      : null;
  }, [currentlyPlayingIndex]);

  if (isLoading) {
    return <InitialLoader progress={loadProgress} />;
  }

  return (
    <div className="page-shell">
      <main className="music-shell">
        {/* HERO */}
        <section className="hero-panel">
          <img
            src={heroImage}
            className="hero-image hero-image--desktop"
            alt="poster"
          />

          <img
            src={heroImageMobile}
            className="hero-image hero-image--mobile"
            alt="poster"
          />
        </section>

        {/* PLAYLIST */}
        <section className="playlist-panel">
          {/* Language Tabs */}
          <div className="language-tabs">
            {languages.map((language) => (
              <button
                key={language}
                onClick={() => setSelectedLanguage(language)}
                className={`language-tab ${selectedLanguage === language ? "active" : ""
                  }`}
              >
                {language}
              </button>
            ))}
          </div>

          {/* Song list */}
          <div className="song-scroll custom-scrollbar">
            <div
              className={`song-list ${currentSong ? "has-player" : ""}`}
            >
              {filteredSongs.length === 0 ? (
                <div className="empty-state">
                  No songs found in this language
                </div>
              ) : (
                filteredSongs.map((song, filteredIndex) => {
                  const actualIndex = songList.findIndex(
                    (s) => s.id === song.id,
                  );
                  const active = actualIndex === currentlyPlayingIndex;

                  return (
                    <button
                      key={`${song.id}-${filteredIndex}`}
                      className={`song-card ${active ? "active" : ""}`}
                      onClick={() => playSong(filteredIndex)}
                    >
                      <span className="song-card__glow" />

                      <span className="song-number">
                        {filteredIndex + 1}
                      </span>

                      <span className="song-play-button" aria-hidden="true">
                        {active && isPlaying ? (
                          <Pause size={18} />
                        ) : (
                          <Play size={18} />
                        )}
                      </span>

                      <span className="song-title">{song.title}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </main>

      {/* PLAYER BAR */}
      {currentSong && (
        <div className="player-dock">
          <div className="player-wrap">
            <footer className="player-bar">
              <div className="player-now">
                <div className="player-title">
                  {currentSong.title}
                </div>
                <div className="player-artist">
                  {currentSong.artist}
                </div>
              </div>

              <div className="player-controls">
                <button
                  className="control-button"
                  onClick={goPrev}
                  aria-label="Previous song"
                >
                  <SkipBack size={20} />
                </button>

                <button
                  className="control-button control-button--primary"
                  onClick={() =>
                    playSong(
                      filteredSongs.findIndex((s) => s.id === currentSong.id),
                    )
                  }
                  aria-label={isPlaying ? "Pause song" : "Play song"}
                >
                  {isPlaying ? (
                    <Pause size={22} />
                  ) : (
                    <Play size={22} />
                  )}
                </button>

                <button
                  className="control-button"
                  onClick={goNext}
                  aria-label="Next song"
                >
                  <SkipForward size={20} />
                </button>
              </div>

              <div className="player-progress">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleSeek}
                  className="progress-slider"
                  style={{ "--value": `${progress}%` }}
                  aria-label="Song progress"
                />
                <span className="track-time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
